import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { calculateRefundPreview, buildRefundRecord, hoursUntilPickup } from '../utils/bookingPolicy';

export default function ManageBookingModal() {
  const {
    modals, closeModal, openModal,
    vehicles, rentals, setRentals,
    setRentalHistory,
    setNotifications,
    companySettings,
    setRefunds,
    dateChangeRequests, setDateChangeRequests,
    showToast,
  } = useApp();

  const open = !!modals['manage-booking'];
  const rentalId = modals['manage-booking-id'];
  const [showDateChange, setShowDateChange] = useState(false);
  const [dcStart, setDcStart] = useState('');
  const [dcEnd, setDcEnd] = useState('');
  const [dcReason, setDcReason] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  if (!open) return null;

  const rental = rentals.find(r => r.id === rentalId);
  if (!rental) return null;

  const v = vehicles.find(x => x.id === rental.vid);
  const s = companySettings || {};

  const statusMap = {
    booked:    { icon: '📅', text: 'Booking Confirmed',   sub: 'Your booking is confirmed — awaiting pickup',  bg: 'var(--bl-l)',  tx: 'var(--bl)',  bd: 'var(--bl-b)',  lbl: 'Confirmed' },
    active:    { icon: '🟢', text: 'Rental Active',        sub: 'Your rental is currently active',               bg: 'var(--gr-l)',  tx: 'var(--gr)',  bd: 'var(--gr-b)',  lbl: 'Active' },
    complete:  { icon: '✅', text: 'Rental Completed',     sub: 'Your rental has been completed',                bg: 'var(--sur2)', tx: 'var(--tx3)', bd: 'var(--bd)',    lbl: 'Completed' },
    cancelled: { icon: '❌', text: 'Booking Cancelled',    sub: 'This booking has been cancelled',               bg: 'var(--ac-l)', tx: 'var(--ac)',  bd: 'var(--ac-b)', lbl: 'Cancelled' },
  };
  const st = statusMap[rental.status] || statusMap.booked;

  const paidOnline = ['stripe', 'online', 'viva', 'paypal'].includes(rental.payMethod);
  const isBankPending  = rental.payMethod === 'bank' && !rental.bankTransferVerified && rental.bankTransferPending !== false;
  const isBankVerified = rental.payMethod === 'bank' && rental.bankTransferVerified;

  function close() {
    closeModal('manage-booking');
    setShowDateChange(false);
    setShowCancelConfirm(false);
  }

  // Compute refund preview from company policy (used by both the confirm screen and submit)
  const refundPreview = rental ? calculateRefundPreview(rental, companySettings) : null;

  function cancelBooking() {
    if (!rental || !refundPreview) return;
    const { finalRefund, refundEligible, transactionFee, refundPercent, deductFee, scenario, originalPaid } = refundPreview;

    // If customer paid (online/bank) AND any refund is due → create a refund queue record
    const needsRefund = paidOnline && finalRefund > 0;
    if (needsRefund) {
      const refundRecord = buildRefundRecord({
        rental: { ...rental, vehicleName: v?.name || rental.vid },
        amount: finalRefund,
        originalAmount: originalPaid,
        scenario,
        reason: `Customer self-cancellation · ${refundPercent}% policy · pickup was ${rental.startLabel}`,
        payMethod: rental.payMethod,
        originalPaymentRef: rental.bookingRef || rental.id,
      });
      setRefunds(prev => [refundRecord, ...prev]);
    }

    // Move rental to history with full snapshot — preserve EVERYTHING (pickup
    // location, times, insurance, extras, payment method, agreement signature,
    // pre-rental photos, customer ID/company/VAT, etc.) and then override only
    // the cancellation-specific fields. This way the History viewer shows the
    // complete record, not a stripped-down stub.
    setRentalHistory(prev => [{
      ...rental,
      id: 'RH-C-' + rental.id,
      contract: rental.id,
      // Some legacy field names the History viewer also looks at
      vehName: v?.name || rental.vid,
      vehIcon: v?.icon || '🚗',
      days: rental.calSpan || rental.days || 0,
      total: rental.agreedPrice || 0,
      depositAmt: rental.deposit || 0,
      depositReturned: needsRefund ? `€${finalRefund.toFixed(2)} pending` : 'Not collected',
      // No return data — booking was cancelled before pickup
      condRet: null, condRetNote: '',
      dmgCharge: 0, dmgNotes: '',
      photosRet: [],
      returnPhotos: [],
      // Cancellation-specific
      status: 'cancelled',
      cancelledAt: new Date().toISOString().slice(0, 10),
      cancelledAtIso: new Date().toISOString(),
      cancelReason: `Cancelled by customer · ${refundPercent}% refund (${scenario})`,
      cancellationSnapshot: {
        originalPaid, refundEligible, transactionFee, deductFee,
        finalRefund, refundPercent, scenario,
        differenceOwedAtCancel: parseFloat(rental.differenceOwed) || 0,
        cancelledAt: new Date().toISOString(),
      },
    }, ...prev]);
    setRentals(prev => prev.filter(r => r.id !== rental.id));

    // Notify operator
    setNotifications(prev => [{
      id: 'N-cancel-' + rental.id,
      type: needsRefund ? 'incoming' : 'incoming',
      icon: needsRefund ? '💸' : '❌',
      title: needsRefund
        ? `Cancellation + refund €${finalRefund.toFixed(2)} — ${rental.bookingRef || rental.id}`
        : `Booking cancelled — ${rental.bookingRef || rental.id}`,
      body: `${rental.customer || 'Customer'} · ${rental.startLabel}${needsRefund ? ` · refund pending (${scenario})` : ' (no charge)'}`,
      time: 'just now', read: false,
      link: needsRefund ? 'refunds' : 'incoming',
    }, ...prev]);

    showToast(needsRefund
      ? `✓ Booking cancelled — €${finalRefund.toFixed(2)} refund pending`
      : 'Booking cancelled — vehicle now available');
    close();
  }

  function submitDateChange() {
    if (!dcStart || !dcEnd) { showToast('Please select new dates'); return; }
    if (new Date(dcEnd) <= new Date(dcStart)) { showToast('Return date must be after pickup date'); return; }

    // Scale the original agreedPrice proportionally to the day-count change.
    // This preserves VAT, insurance, extras, location fees (all baked into agreedPrice).
    // Using rate × days would strip VAT/extras and produce false refunds.
    const oldDays = Math.max(1, Math.round((new Date(rental.end) - new Date(rental.start)) / 86400000));
    const newDays = Math.max(1, Math.round((new Date(dcEnd) - new Date(dcStart)) / 86400000));
    const newPriceEst = Math.round((rental.agreedPrice || 0) * (newDays / oldDays) * 100) / 100;
    const priceDiff = Math.round((newPriceEst - (rental.agreedPrice || 0)) * 100) / 100;

    const requestRecord = {
      id: 'dcr-' + Date.now().toString(36).slice(-8),
      rentalId:        rental.id,
      bookingRef:      rental.bookingRef || null,
      customer:        rental.customer || '',
      email:           rental.email || '',
      vid:             rental.vid,
      oldStart:        rental.start, oldEnd: rental.end,
      oldStartLabel:   rental.startLabel, oldEndLabel: rental.endLabel,
      oldDays, oldPrice: rental.agreedPrice || 0,
      newStart:        dcStart, newEnd: dcEnd,
      newDays, newPriceEst, priceDiff,
      reason:          dcReason || '',
      status:          'pending',     // 'pending' | 'accepted' | 'refused'
      createdAt:       new Date().toISOString(),
      decidedAt:       null, decidedBy: null, decisionNote: null,
    };
    setDateChangeRequests(prev => [requestRecord, ...prev]);

    setNotifications(prev => [{
      id: 'N-dc-' + requestRecord.id, type: 'incoming', icon: '📅',
      title: `Date change request — ${rental.bookingRef || rental.id}`,
      body: `${rental.customer || 'Customer'} → ${dcStart} to ${dcEnd} · ${priceDiff > 0 ? `+€${priceDiff}` : priceDiff < 0 ? `−€${Math.abs(priceDiff)}` : 'same price'}`,
      time: 'just now', read: false, link: 'incoming',
    }, ...prev]);

    showToast('✓ Request sent — you\'ll receive an email once reviewed (usually within 24h)');
    setShowDateChange(false);
    close();
  }

  // ── Policy gates for the date-change button on the main view ──────────────
  const cutoffHours = parseInt(companySettings?.dateChangeCutoffHours) || 24;
  const maxChanges  = parseInt(companySettings?.maxDateChanges) || 1;
  const hoursToPickup = rental ? hoursUntilPickup(rental) : 0;
  const existingChanges = (dateChangeRequests || []).filter(d => d.rentalId === rental?.id && (d.status === 'pending' || d.status === 'accepted')).length;
  const tooCloseToPickup = hoursToPickup < cutoffHours;
  const exceedsMaxChanges = existingChanges >= maxChanges;
  const canRequestDateChange = rental?.status === 'booked' && !tooCloseToPickup && !exceedsMaxChanges;
  const dateChangeBlockedReason = tooCloseToPickup
    ? `Locked — within ${cutoffHours}h of pickup. Please call to change.`
    : exceedsMaxChanges
    ? `Already used ${existingChanges} of ${maxChanges} change${maxChanges === 1 ? '' : 's'}. Please call.`
    : null;

  const row = (icon, label, val) => (
    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
      <span style={{ color: 'var(--tx3)' }}>{icon} {label}</span>
      <span style={{ fontWeight: 500 }}>{val}</span>
    </div>
  );

  const payLabel = rental.payMethod === 'cash' ? '💵 Cash' : rental.payMethod === 'bank' ? '🏦 Bank Transfer' : '💳 Card';

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  // Date change sub-modal
  if (showDateChange) {
    return (
      <div style={overlayStyle} onClick={() => setShowDateChange(false)}>
        <div style={{ width: 460, maxWidth: '96vw', background: 'var(--sur)', borderRadius: 'var(--rl)', boxShadow: '0 20px 60px rgba(0,0,0,.3)', overflow: 'hidden' }}
          onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--bd)' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--am-l)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📅</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Request Date Change</div>
              <div style={{ fontSize: 11, color: 'var(--tx3)' }}>{rental.bookingRef || rental.id} · {rental.startLabel} → {rental.endLabel}</div>
            </div>
            <button onClick={() => setShowDateChange(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--tx3)', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--bl)', background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 'var(--r)', padding: '10px 12px', marginBottom: 12 }}>
              ℹ️ Your request will be sent to the company. They will confirm availability and contact you within 24 hours.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label className="fl">New Pickup Date *</label>
                <input type="date" className="fi" value={dcStart} onChange={e => setDcStart(e.target.value)} />
              </div>
              <div>
                <label className="fl">New Return Date *</label>
                <input type="date" className="fi" value={dcEnd} onChange={e => setDcEnd(e.target.value)} />
              </div>
            </div>
            <div className="fg">
              <label className="fl">Reason / Message (optional)</label>
              <textarea className="fi" rows={2} value={dcReason} onChange={e => setDcReason(e.target.value)}
                placeholder="e.g. My plans changed, I need the vehicle one day earlier..." />
            </div>

            {/* Live price preview — scales original price proportionally so VAT/extras stay baked in */}
            {dcStart && dcEnd && new Date(dcEnd) > new Date(dcStart) && (() => {
              const oldDays = Math.max(1, Math.round((new Date(rental.end) - new Date(rental.start)) / 86400000));
              const newDays = Math.max(1, Math.round((new Date(dcEnd) - new Date(dcStart)) / 86400000));
              const newPriceEst = Math.round((rental.agreedPrice || 0) * (newDays / oldDays) * 100) / 100;
              const diff = Math.round((newPriceEst - (rental.agreedPrice || 0)) * 100) / 100;
              return (
                <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 6, fontSize: 11 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: 'var(--tx3)' }}>Original ({oldDays} day{oldDays === 1 ? '' : 's'})</span>
                    <span>€{(rental.agreedPrice || 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: 'var(--tx3)' }}>New estimate ({newDays} day{newDays === 1 ? '' : 's'})</span>
                    <span>€{newPriceEst.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, borderTop: '1px solid var(--bd)', fontWeight: 700 }}>
                    <span>Difference</span>
                    <span style={{ color: diff > 0 ? 'var(--am)' : diff < 0 ? 'var(--gr)' : 'var(--tx2)' }}>
                      {diff > 0 ? `+€${diff.toFixed(2)} owed at pickup` : diff < 0 ? `−€${Math.abs(diff).toFixed(2)} refund due` : 'Same price'}
                    </span>
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--tx3)', marginTop: 6, fontStyle: 'italic' }}>
                    Estimate — operator will confirm the exact amount (including any VAT, extras or seasonal pricing) when approving.
                  </div>
                </div>
              );
            })()}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '10px 16px', borderTop: '1px solid var(--bd)' }}>
            <button className="btn btn-ghost" onClick={() => setShowDateChange(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitDateChange}>Send Request</button>
          </div>
        </div>
      </div>
    );
  }

  // Cancel confirmation sub-modal — shows policy-driven refund breakdown
  if (showCancelConfirm) {
    const rp = refundPreview;
    const hoursBefore = rp?.hoursBefore ?? 0;
    const fmtHours = hoursBefore < 0 ? 'after pickup time' : hoursBefore < 24 ? `in ${Math.round(hoursBefore)}h` : `in ${Math.round(hoursBefore / 24)} day${Math.round(hoursBefore / 24) === 1 ? '' : 's'}`;
    return (
      <div style={overlayStyle} onClick={() => setShowCancelConfirm(false)}>
        <div style={{ width: 460, maxWidth: '96vw', background: 'var(--sur)', borderRadius: 'var(--rl)', boxShadow: '0 20px 60px rgba(0,0,0,.3)', overflow: 'hidden' }}
          onClick={e => e.stopPropagation()}>
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--bd)' }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Cancel Booking</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>
              {rental.bookingRef || rental.id} · pickup {fmtHours}
            </div>
          </div>
          <div style={{ padding: 16 }}>
            {rp && paidOnline ? (
              <>
                {/* Refund breakdown card */}
                <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px', marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: 'var(--tx3)' }}>You paid</span>
                    <span style={{ fontWeight: 600 }}>€{rp.originalPaid.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: 'var(--tx3)' }}>
                      Refund per policy ({rp.refundPercent}%)
                    </span>
                    <span style={{ fontWeight: 600, color: rp.refundPercent === 100 ? 'var(--gr)' : rp.refundPercent === 0 ? 'var(--ac)' : 'var(--am)' }}>
                      €{rp.refundEligible.toFixed(2)}
                    </span>
                  </div>
                  {rp.transactionFee > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 6, color: 'var(--tx3)' }}>
                      <span>− Processing fee ({rp.feePercent}%)</span>
                      <span>− €{rp.transactionFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid var(--bd)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                      You'll receive
                    </span>
                    <span style={{ fontSize: 22, fontWeight: 800, color: rp.finalRefund > 0 ? 'var(--gr)' : 'var(--ac)', fontFamily: "'DM Mono',monospace" }}>
                      €{rp.finalRefund.toFixed(2)}
                    </span>
                  </div>
                  {rp.finalRefund > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 6, textAlign: 'right' }}>
                      Processed within 5–7 business days
                    </div>
                  )}
                  {rp.finalRefund === 0 && (
                    <div style={{ fontSize: 10, color: 'var(--ac)', marginTop: 6, textAlign: 'right' }}>
                      Cancellation is outside the free-cancellation window
                    </div>
                  )}
                </div>
                {s.cancelPolicy && (
                  <div style={{ fontSize: 10, color: 'var(--tx3)', lineHeight: 1.5, background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 6, padding: '8px 10px' }}>
                    <strong style={{ color: 'var(--bl)' }}>Our cancellation policy:</strong> {s.cancelPolicy}
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--tx2)' }}>
                Cancel your booking for <strong>{rental.startLabel}</strong>? No charge applies — nothing was prepaid.
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '10px 16px', borderTop: '1px solid var(--bd)' }}>
            <button className="btn btn-ghost" onClick={() => setShowCancelConfirm(false)}>Keep Booking</button>
            <button className="btn" style={{ background: 'var(--ac)', color: '#fff', border: '1px solid var(--ac)' }} onClick={cancelBooking}>
              {rp && rp.finalRefund > 0 ? `Cancel & refund €${rp.finalRefund.toFixed(2)}` : 'Cancel Booking'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={close}>
      <div style={{ width: 560, maxWidth: '96vw', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'var(--sur)', borderRadius: 'var(--rl)', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--bd)', flexShrink: 0 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bl-l)', border: '1px solid var(--bl-b)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--bl)', fontSize: 16 }}>📋</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Manage Your Booking</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Ref: {rental.bookingRef || rental.id}</div>
          </div>
          <button onClick={close} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--tx3)', lineHeight: 1 }}>×</button>
        </div>

        {/* Status banner */}
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--bd)', background: st.bg, flexShrink: 0 }}>
          <div style={{ fontSize: 24 }}>{st.icon}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{st.text}</div>
            <div style={{ fontSize: 11, color: 'var(--tx2)' }}>{st.sub}</div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: st.bg, color: st.tx, border: `1px solid ${st.bd}` }}>{st.lbl}</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>

          {/* Booking summary */}
          <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', padding: 14, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--bd)' }}>
              <div style={{ fontSize: 32 }}>{v?.icon || '🚗'}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{v?.name || rental.vid}</div>
                <div style={{ fontSize: 11, color: 'var(--tx3)' }}>{v?.type || ''}{v?.seats ? ' · ' + v.seats : ''}</div>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gr)', fontFamily: "'DM Mono',monospace" }}>€{rental.agreedPrice}</div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Total</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {row('📅', 'Pickup',  rental.startLabel)}
              {row('📅', 'Return',  rental.endLabel)}
              {row('📍', 'Pickup point', rental.pickupLocName || '—')}
              {rental.returnLocName && rental.returnLocName !== rental.pickupLocName && row('📍', 'Return point', rental.returnLocName)}
              {rental.insurance && row('🛡️', 'Insurance', rental.insurance)}
              {rental.extras?.length > 0 && row('➕', 'Extras', rental.extras.join(', '))}
              {row('💳', 'Payment', payLabel)}
              {row('🔑', 'Ref', rental.bookingRef || rental.id)}
            </div>
          </div>

          {/* Bank transfer notice */}
          {isBankVerified && (
            <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 'var(--r)', padding: '10px 12px', marginBottom: 14, fontSize: 11, color: 'var(--gr)', fontWeight: 600 }}>
              ✅ Bank transfer payment confirmed — pro forma will be marked as paid at check-in
            </div>
          )}
          {isBankPending && (
            <div style={{ background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 'var(--r)', padding: '10px 12px', marginBottom: 14, fontSize: 11, color: 'var(--am)' }}>
              🏦 <strong>Awaiting payment confirmation</strong><br />
              <span style={{ fontSize: 10, color: 'var(--tx2)' }}>We'll confirm your booking once we receive your bank transfer. Check your email for IBAN details.</span>
            </div>
          )}

          {/* What to bring — deposit reflects the selected insurance plan (0 = none) */}
          {(() => {
            const dep = parseFloat(rental.deposit) || 0;
            return (
              <div style={{ background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 'var(--r)', padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--am)', marginBottom: 6 }}>📋 What to bring on pickup day</div>
                <div style={{ fontSize: 11, color: 'var(--tx2)', lineHeight: 1.7 }}>
                  ✓ Original Passport or National ID<br />
                  ✓ Valid Driving Licence<br />
                  {dep > 0
                    ? <>✓ Payment method for the refundable deposit: <strong>€{dep.toFixed(0)}</strong>{rental.insurance ? <> ({rental.insurance})</> : null}<br /></>
                    : <>✓ <strong>No deposit required</strong>{rental.insurance ? <> — covered by {rental.insurance}</> : null}<br /></>}
                  ✓ This booking reference: <strong>{rental.bookingRef || rental.id}</strong>
                </div>
              </div>
            );
          })()}

          {/* Company contact */}
          <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>📞 Questions? Contact us</div>
            <div style={{ fontSize: 12, color: 'var(--tx2)', lineHeight: 1.7 }}>
              <strong>{s.name || 'FleetRent Cyprus'}</strong><br />
              {s.phone && <>📞 {s.phone}<br /></>}
              {s.email && <>✉️ {s.email}<br /></>}
              {s.address && <>📍 {s.address}</>}
            </div>
          </div>

          {/* Actions */}
          {rental.status === 'booked' && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Booking Options</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button className="btn btn-ghost" disabled={!canRequestDateChange}
                  onClick={() => {
                    if (!canRequestDateChange) return;
                    setDcStart(rental.start?.slice(0, 10) || '');
                    setDcEnd(rental.end?.slice(0, 10) || '');
                    setDcReason('');
                    setShowDateChange(true);
                  }}
                  style={{ justifyContent: 'flex-start', gap: 8, opacity: canRequestDateChange ? 1 : 0.55, cursor: canRequestDateChange ? 'pointer' : 'not-allowed' }}>
                  📅 Request Date Change
                  <span style={{ fontSize: 10, marginLeft: 'auto', color: canRequestDateChange ? 'var(--tx3)' : 'var(--ac)' }}>
                    {dateChangeBlockedReason || 'We\'ll confirm within 24h'}
                  </span>
                </button>
                <button className="btn btn-ghost" onClick={() => setShowCancelConfirm(true)}
                  style={{ justifyContent: 'flex-start', gap: 8, color: 'var(--ac)', borderColor: 'var(--ac-b)' }}>
                  ❌ Cancel Booking
                  <span style={{ fontSize: 10, marginLeft: 'auto', color: refundPreview && refundPreview.finalRefund > 0 ? 'var(--gr)' : refundPreview && refundPreview.refundPercent < 100 ? 'var(--am)' : 'var(--tx3)' }}>
                    {!paidOnline ? 'No charge'
                      : refundPreview && refundPreview.finalRefund > 0 ? `Refund: €${refundPreview.finalRefund.toFixed(2)}`
                      : 'No refund (outside policy)'}
                  </span>
                </button>
              </div>
            </div>
          )}

          {rental.status !== 'booked' && (
            <div style={{ fontSize: 11, color: 'var(--tx3)', padding: '8px 0' }}>Contact us if you need assistance.</div>
          )}

          {/* Policies */}
          {(s.cancelPolicy || s.latePolicy) && (
            <div style={{ borderTop: '1px solid var(--bd)', paddingTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Our Policies</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {s.cancelPolicy && (
                  <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
                    <div style={{ background: 'var(--sur2)', padding: '6px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tx2)' }}>❌ Cancellation Policy</div>
                    <div style={{ padding: '8px 10px', fontSize: 11, color: 'var(--tx2)', lineHeight: 1.6 }}>{s.cancelPolicy}</div>
                  </div>
                )}
                {s.latePolicy && (
                  <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
                    <div style={{ background: 'var(--sur2)', padding: '6px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tx2)' }}>⏰ Late Return Policy</div>
                    <div style={{ padding: '8px 10px', fontSize: 11, color: 'var(--tx2)', lineHeight: 1.6 }}>{s.latePolicy}</div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '10px 16px', borderTop: '1px solid var(--bd)', flexShrink: 0 }}>
          <button className="btn btn-ghost" onClick={close}>Close</button>
          <button className="btn btn-ghost" onClick={() => showToast('Booking details printed')}>🖨️ Print</button>
        </div>

      </div>
    </div>
  );
}
